// This component displays a big widget in the center of the homepage. It is NOT a sticky banner along the top; see BiAnnualSaleSuperheader for that (which only runs on posts and generators, not the homepage).
'use client';

import * as React from 'react';
import { styled } from '@linaria/react';
import { ArrowRight } from 'lucide-react';

import { DARK_COLORS, BREAKPOINTS, Promo } from '@/constants';

import ButtonPlayful from '@/components/ButtonPlayful';
import TextLink from '@/components/Link/TextLink';

import JoyLogo from './JoyLogo';

function BiAnnualSalePromo({ promo }: { promo: Promo }) {
  const saleName =
    promo === 'black-friday' ? 'Black Friday sale' : 'Spring Sale';
  const emoji = promo === 'black-friday' ? '🎈' : '🌸';
  // HACK: I want the promo to show up slightly later than the rest of the page, so that everything doesn't happen all at once. The problem is that synchronizing the logo animation with the parent wrapper animation has been stupidly difficult.
  // My fix for now is to re-mount the JoyLogo when the animation starts, and to trigger all of this with a state variable:
  const [isVisible, setIsVisible] = React.useState(false);

  React.useEffect(() => {
    window.setTimeout(() => {
      setIsVisible(true);
    }, 500);
  }, []);

  return (
    <Wrapper data-is-visible={isVisible ? 'true' : 'false'}>
      <StyledLogo key={isVisible ? 'yes' : 'no'} />
      <p>
        <strong>
          {emoji} It’s the {saleName}!
        </strong>{' '}
        For a limited time, you can save a whopping $500 on my two
        flagship courses,{' '}
        <TextLink href="https://css-for-js.dev">
          CSS for JavaScript Developers
        </TextLink>{' '}
        and{' '}
        <TextLink href="https://joyofreact.com">
          The Joy of React
        </TextLink>
        .
      </p>
      <p>
        I know that lots of online courses go on sale every other
        week, but I only run sales twice a year. So this really is a
        rare chance to snag a big discount. 😄
      </p>
      <LinkButton
        Icon={ArrowRight}
        iconSide="right"
        textAlign="true-center"
        href="https://joyforjs.com"
        backgroundColor="var(--button-bg)"
        highlightColor="var(--button-highlight)"
        boopEffect={{
          x: 2,
        }}
      >
        Learn More
      </LinkButton>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  --selection-background-color: hsl(48deg 100% 60%);
  --selection-text-color: black;

  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  border: 4px dashed oklch(0.87 0.21 91.16);
  margin-bottom: 96px;
  margin-top: -8rem;
  margin-inline: auto;
  padding: 2rem;
  border-radius: 32px;
  background: var(--color-background);
  outline: 6px solid var(--color-background);
  transition: opacity 200ms;

  html[data-color-mode='light'] & {
    --color-background: hsl(48deg 100% 90%);
    --color-shadow: 30deg 100% 30%;

    transition: --color-background 400ms;
    border: 4px dashed hsl(210deg 15% 6%);

    box-shadow:
      0px 1px 2px hsl(var(--color-shadow) / 0.3),
      0px 3px 6px hsl(var(--color-shadow) / 0.3),
      0px 9px 18px hsl(var(--color-shadow) / 0.3);
  }

  &[data-is-visible='false'] {
    opacity: 0;
    transform: scale(0.9);
    pointer-events: none;
  }
  &[data-is-visible='true'] {
    opacity: 1;
    transform: scale(1);
    transition: transform 600ms cubic-bezier(0.17, 0.67, 0.39, 1);
  }

  @media ${BREAKPOINTS.mdAndSmaller} {
    padding: 1.5rem;
    padding-top: 2rem;
    border-radius: 24px;
  }
  @media (max-width: 24rem) {
    display: none;
  }

  @media (prefers-reduced-motion: reduce) {
    &[data-is-visible='true'] {
      transition: transform 0ms cubic-bezier(0.17, 0.67, 0.39, 1);
    }
  }

  p {
    font-size: 1.125rem !important;
  }
`;

const StyledLogo = styled(JoyLogo)`
  align-self: center;
  width: 100%;
  max-width: 25rem;
  margin-top: -28%;
  margin-bottom: 1rem;
`;

const LinkButton = styled(ButtonPlayful)`
  --button-bg: oklch(0.87 0.21 91.16);
  --button-highlight: hsl(48deg 100% 75%);
  width: 100%;
  max-width: 22rem;
  margin-top: 1rem;
  margin-inline: auto;
  color: black !important;
  outline-color: oklch(0.87 0.21 91.16) !important;

  &:hover {
    color: black;
    text-decoration: none;
  }

  html[data-color-mode='light'] & {
    --button-bg: ${DARK_COLORS['--color-background']};
    --button-highlight: var(--color-gray-800);
    color: white !important;

    &:hover {
      color: white;
    }
  }
`;

export default BiAnnualSalePromo;
